<template>
  <div class="page-layout">
    <flash-message transitionIn="animated swing" class="alert-system" />

    <div v-if="loading" class="container" style="margin-top: 55px; min-height: 100dvh">
      <ProgressLoading :loading="loading" />
    </div>

    <template v-else-if="isAuthenticatedView">
      <PageHeader
        :current-step="currentStep"
        :brand="currentBrand"
        :show-steps="showPageHeaderSteps"
        :user-name="negotiationUserFullName"
        :logout-from="negotiationLogoutFrom"
        @logout="onLogoutFromHeader"
      />
      <main class="page-content">
        <HomeDashboard v-if="currentView === 'dashboard'" />
        <NegotiationBoticario v-else-if="currentView === 'negotiationBoticario'" />
        <NegotiationEudora v-else-if="currentView === 'negotiationEudora'" />
        <PaymentScreen v-else-if="currentView === 'paymentBoticario'" brand="boticario" />
        <PaymentScreen v-else-if="currentView === 'paymentEudora'" brand="eudora" />
        <AgreementSuccess
          v-else-if="currentView === 'finishBoticario'"
          :agreement="agreementSafe"
          :prepare-to-set-agreement="prepareSafe"
          :selected-payment-method="selectedPaymentMethod"
          brand="boticario"
          @return-to-dashboard="onReturnToDashboardAfterAgreement"
          @copied="onCopied"
          @final-state-change="onFinalStateChange"
        />
        <AgreementSuccess
          v-else-if="currentView === 'finishEudora'"
          :agreement="agreementSafe"
          :prepare-to-set-agreement="prepareSafe"
          :selected-payment-method="selectedPaymentMethod"
          brand="eudora"
          @return-to-dashboard="onReturnToDashboardAfterAgreement"
          @copied="onCopied"
          @final-state-change="onFinalStateChange"
        />
        <AgreementBillDetail v-else-if="currentView === 'agreementBillDetail'" />
      </main>
      <div class="mobile-logout">
        <button type="button" class="mobile-logout__button" @click="onLogoutFromHeader">
          <i class="fa fa-sign-out mr-2" aria-hidden="true" />
          Encerrar sessão
        </button>
      </div>
      <PageFooter />
    </template>

    <template v-else-if="currentView === 'notLogged'">
      <header class="generic-login__header">
        <div class="brand-strip">
          <div class="brand-strip__segment brand-strip__segment--boticario"></div>
          <div class="brand-strip__segment brand-strip__segment--qdb"></div>
          <div class="brand-strip__segment brand-strip__segment--oui"></div>
          <div class="brand-strip__segment brand-strip__segment--eudora"></div>
        </div>
        <div class="generic-login__header-inner">
          <img src="/img/logo.png" alt="Grupo Boticário" class="generic-login__logo" />
        </div>
      </header>
      <main class="page-content page-content--not-logged">
        <div class="not-logged-message">
          <h4 class="not-logged-message__title">
            {{ notLoggedText }}
          </h4>
          <hr />
        </div>
      </main>
      <PageFooter />
    </template>

    <CampaignPopup
      :visible="showCampaignPopup"
      image="/img/beautyweek-desktop.jpeg"
      image-mobile="/img/beautyweek-mobile.jpeg"
      alt="Beauty Week Grupo Boticário"
      @close="showCampaignPopup = false"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import ProgressLoading from '@/modules/auth/components/ProgressLoading';
import HomeDashboard from '@/pages/home/components/HomeDashboard.vue';
import NegotiationBoticario from '@/pages/home/components/NegotiationBoticario.vue';
import NegotiationEudora from '@/pages/home/components/NegotiationEudora.vue';
import PaymentScreen from '@/pages/home/components/PaymentScreen.vue';
import AgreementSuccess from '@/pages/home/components/AgreementSuccess.vue';
import AgreementBillDetail from '@/pages/home/components/AgreementBillDetail.vue';
import PageHeader from '@/pages/home/components/PageHeader.vue';
import PageFooter from '@/pages/home/components/PageFooter.vue';
import CampaignPopup from '@/pages/home/components/CampaignPopup.vue';
import {
  consumeCampaignPopupIfNeeded,
  markCampaignPopupPending,
} from '@/pages/home/utils/campaignPopup';

export default {
  name: 'HomeLayout',
  data: () => ({
    hideStepIndicator: false,
    showCampaignPopup: false,
  }),
  components: {
    ProgressLoading,
    HomeDashboard,
    NegotiationBoticario,
    NegotiationEudora,
    PaymentScreen,
    AgreementSuccess,
    AgreementBillDetail,
    PageHeader,
    PageFooter,
    CampaignPopup,
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('negotiation', [
      'loading', 'notLoggedText', 'hasNoBoticarioData',
      'agreement', 'prepareToSetAgreement', 'selectedPaymentMethod',
    ]),
    ...mapGetters('negotiation', [
      'currentView',
      'currentBrand',
      'isAuthenticatedView',
      'currentStep',
      'showNegotiationStepperOnDashboard',
    ]),

    showPageHeaderSteps() {
      if (this.currentView === 'agreementBillDetail') {
        return false;
      }
      if (this.currentView === 'dashboard') {
        return this.showNegotiationStepperOnDashboard;
      }
      return !this.hideStepIndicator;
    },

    displayName() {
      let name = this.user?.FullName;
      if (!name) {
        try {
          const stored = localStorage.getItem('user');
          if (stored) name = JSON.parse(stored)?.FullName;
        } catch { name = null; }
      }
      const normalized = typeof name === 'string' ? name.trim() : '';
      const doc = this.user?.DocNumber ? String(this.user.DocNumber).replace(/\D/g, '') : '';
      const nameDigits = normalized.replace(/\D/g, '');
      const isJustDocument = normalized !== '' && (
        normalized === this.user?.DocNumber
        || (doc !== '' && nameDigits === doc)
      );
      if (normalized === '' || isJustDocument) return '';
      return normalized.includes(' ') ? normalized.split(' ')[0] : normalized;
    },
    negotiationUserFullName() {
      let name = this.user?.FullName;
      if (!name) {
        try {
          const stored = localStorage.getItem('user');
          if (stored) name = JSON.parse(stored)?.FullName;
        } catch { name = null; }
      }
      const normalized = typeof name === 'string' ? name.trim() : '';
      const doc = this.user?.DocNumber ? String(this.user.DocNumber).replace(/\D/g, '') : '';
      const nameDigits = normalized.replace(/\D/g, '');
      const isJustDocument = normalized !== '' && (
        normalized === this.user?.DocNumber
        || (doc !== '' && nameDigits === doc)
      );
      if (normalized === '' || isJustDocument) return '';
      return normalized;
    },
    negotiationLogoutFrom() {
      return this.currentBrand === 'eudora' ? 'Eudora' : 'Boticario';
    },
    agreementSafe() {
      return this.agreement && typeof this.agreement === 'object' ? this.agreement : {};
    },
    prepareSafe() {
      return this.prepareToSetAgreement && typeof this.prepareToSetAgreement === 'object'
        ? this.prepareToSetAgreement
        : {};
    },
  },
  methods: {
    ...mapActions('negotiation', [
      'ActionNegotiationGetIPAddress',
      'ActionNegotiationGetDebts',
      'ActionNegotiationVerifyDirectAccess',
      'ActionNegotiationLogout',
      'ActionNegotiationGoToDashboard',
    ]),
    ...mapActions('auth', ['ActionDoLogout']),

    maybeShowCampaignPopup() {
      if (!consumeCampaignPopupIfNeeded()) return;
      this.$nextTick(() => {
        this.showCampaignPopup = true;
      });
    },

    onEntendi() {
      window.location.reload();
    },
    async onLogoutFromHeader() {
      await this.ActionNegotiationLogout();
      this.$router.replace({ name: 'genericLogin' }).catch(() => {});
    },

    async onReturnToDashboardAfterAgreement() {
      this.hideStepIndicator = false;
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Após fechar acordo, refazemos getDebts para refletir o novo acordo
      // na seção "Meus acordos" do dashboard.
      await this.ActionNegotiationGoToDashboard({ refresh: true });
    },
    onCopied() {
      this.flashMessage.show({ timeout: 1000, status: 'success', title: 'Sucesso', message: 'Código copiado com sucesso' });
    },
    onFinalStateChange(hide) {
      this.hideStepIndicator = !!hide;
    },

    async refreshNegotiationDebtsQuietly() {
      if (!this.user) return;
      try {
        await this.ActionNegotiationGetDebts(null);
      } catch {
        return;
      }
    },

    async initSession() {
      this.hideStepIndicator = false;
      this.$store.commit('negotiation/SET_LOADING', true);
      await this.ActionNegotiationGetIPAddress();

      if (this.user) {
        this.$store.commit('negotiation/SET_DOC_NUMBER', this.user.DocNumber);
        this.$store.commit('negotiation/SET_DEBTOR_ID', this.user.DebtorID);
        let dashboardReady = false;
        try {
          await this.ActionNegotiationGetDebts(null);
          this.$store.commit('negotiation/SET_VIEW', 'dashboard');
          dashboardReady = true;
        } catch {
          this.$store.commit('negotiation/SET_VIEW', 'notLogged');
          await this.ActionDoLogout();
        } finally {
          this.$store.commit('negotiation/SET_LOADING', false);
        }
        if (dashboardReady) this.maybeShowCampaignPopup();
        return;
      }

      const hasMailingParam = this.$route.params && this.$route.params.mailing;
      const isGenericLoginRoute = this.$route.name === 'genericLogin';
      const isLandingRoute = this.$route.name === 'landing';

      if (this.$route.params.shortCode) {
        // DX-2201: link curto (?codePass= antigo virou baseURL/{código}) — manda
        // o código pro backend resolver via ShortLinks, sem encryptedData/queryType.
        await this.handleDirectAccess(null, 'shortCode', this.$route.params.shortCode);
      } else if (this.$route.query.codePass) {
        const encryptedData = this.$route.query.codePass.replaceAll(' ', '+');
        await this.handleDirectAccess(encryptedData, 'codePass');
      } else if (this.$route.query.cod) {
        const infoEncrypted = this.$route.query.cod;
        localStorage.setItem('infoEncrypted', infoEncrypted);
        localStorage.removeItem('encryptedData');
        await this.handleDirectAccess(infoEncrypted, 'cod');
      } else if (hasMailingParam || isLandingRoute) {
        this.$store.commit('negotiation/SET_LOADING', false);
      } else if (!isGenericLoginRoute) {
        this.$store.commit('negotiation/SET_LOADING', false);
        this.$router.push({ name: 'genericLogin' });
      } else {
        this.$store.commit('negotiation/SET_LOADING', false);
      }
    },

    async handleDirectAccess(encryptedData, queryType, shortCode = null) {
      try {
        const response = await this.ActionNegotiationVerifyDirectAccess({ encryptedData, queryType, shortCode });

        if (response.success) {
          if (response.userSiscobra) {
            this.$store.commit('negotiation/SET_LOADING', false);
            this.$router.push({
              name: 'siscobra',
              params: {
                DebtorID: response.debtorId,
                UserID: response.userAdmId,
                FullName: response.FullName,
              },
            });
            return;
          }

          this.$store.commit('negotiation/SET_DOC_NUMBER', response.docNumber);
          try {
            await this.ActionNegotiationGetDebts(null);
            this.$store.commit('negotiation/SET_VIEW', 'dashboard');
            markCampaignPopupPending();
            this.$nextTick(() => this.maybeShowCampaignPopup());
          } catch {
            this.$store.commit('negotiation/SET_VIEW', 'notLogged');
            await this.ActionDoLogout();
          }
        } else {
          // DX-3947: quando a API sinaliza link expirado (mensagem exata enviada
          // só para Boticário/Eudora, ver DebtorLinkAccessService::getLinkDirectAccessV2),
          // mostra essa causa específica na tela de "não logado" em vez do texto
          // genérico padrão. response.message raramente vem preenchido aqui — a
          // mensagem real da API, quando existe, está em response.data.message.
          const apiMessage = response.message || response.data?.message;
          if (apiMessage === 'Link expirado.') {
            this.$store.commit('negotiation/SET_NOT_LOGGED_TEXT', 'Link expirado. Solicite um novo acesso.');
          }
          this.flashMessage.show({ timeout: 300, status: 'error', position: 'top right', title: 'Erro', message: response.message });
          this.$store.commit('negotiation/SET_VIEW', 'notLogged');
        }
      } catch {
        this.$store.commit('negotiation/SET_VIEW', 'notLogged');
      } finally {
        this.$store.commit('negotiation/SET_LOADING', false);
      }
    },
  },
  async created() {
    await this.initSession();
  },
  watch: {
    currentView(view) {
      if (view !== 'finishBoticario' && view !== 'finishEudora') {
        this.hideStepIndicator = false;
      }
    },
    $route(to, from) {
      if (to.name !== 'home' || !this.user) return;
      if (!from || !from.name || from.name === 'home') return;
      this.refreshNegotiationDebtsQuietly();
    },
  },
};
</script>

<style>
:root {
  --negotiation-card-shadow:
    0 1px 2px rgba(0, 0, 0, 0.04),
    0 8px 18px -6px rgba(0, 0, 0, 0.13);
  --negotiation-layout-max-width: 960px;
}

body, .v-application {
  font-family: 'Inter', sans-serif !important;
  background-color: #fff !important;
}

.page-layout {
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
}

.page-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  padding: 12px 24px 0;
  box-sizing: border-box;
}

@media (min-width: 768px) {
  .page-content {
    max-width: var(--negotiation-layout-max-width, 960px);
    padding-left: calc(
      24px + (min(var(--negotiation-layout-max-width, 960px), 100vw) - 48px) / 8 - 18px
    );
  }
}

@media (max-width: 767px) {
  .page-content {
    padding: 10px 16px 0;
  }
}

.brand-strip {
  display: flex;
  width: 100%;
  height: 10px;
}

.brand-strip__segment {
  flex: 1;
}

.brand-strip__segment--boticario {
  background: #2da072;
}

.brand-strip__segment--qdb {
  background: #d245ad;
}

.brand-strip__segment--oui {
  background: #8d2231;
}

.brand-strip__segment--eudora {
  background: #613a53;
}

.generic-login__header {
  background: #fff;
  border-bottom: 1px solid #dfdfdf;
}

.generic-login__header-inner {
  height: 82px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.generic-login__logo {
  width: 146px;
  height: auto;
}

.page-content--not-logged {
  min-height: 40vh;
  justify-content: flex-start;
  padding: 28px 24px 0 !important;
}

.not-logged-message {
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  text-align: center;
}

.not-logged-message__title {
  color: #000;
  font-weight: 700;
  margin-bottom: 20px;
}

.mobile-logout {
  display: none;
}

@media (max-width: 767px) {
  .mobile-logout {
    display: block;
    width: 100%;
    padding: 0 16px 14px;
    box-sizing: border-box;
  }

  .mobile-logout__button {
    width: 100%;
    min-height: 44px;
    border: 1px solid #d0d0d0;
    border-radius: 10px;
    background: #fff;
    color: #333438;
    font-family: 'Inter', sans-serif;
    font-size: 0.95rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
}

.text-info {
  word-wrap: break-word;
  white-space: pre-wrap;
  max-width: 100%;
}
select {
  width: 100%;
  padding: 10px;
  margin: 10px 0 5px 0;
  border-radius: 8px;
  border: 1px solid #06a472;
  background-color: #fff;
  color: #333438;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23333438' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 12px;
  outline: none;
}
select:focus {
  border-color: #176e53;
  box-shadow: 0 0 5px rgba(23, 110, 83, 0.3);
}
.selected-option {
  background-color: #f0f8f0;
  border: 2px solid #06a472;
  border-radius: 8px;
  padding: 12px;
  font-weight: 600;
}
.selected-option-2 {
  background-color: #f0f8f0;
  border: 2px solid #06a472;
  border-radius: 8px;
  padding: 12px;
  font-weight: 600;
}
.price { color: #176e53; font-weight: bold; }
.price-2 { color: #176e53; font-weight: bold; }
.codebar {
  word-break: break-all;
  font-size: 0.85rem;
  padding: 8px;
  flex: 1;
}
.text-wrapper-10 { font-size: 1rem; font-weight: 600; }
.vencimento-do-vista { margin-top: 8px; }
</style>
